import { ColorPicker } from '@/components/ColorPicker';
import { removeHexColorAlphaSuffix } from '@/components/ColorPicker/utils/removeHexColorAlphaSuffix';
import { useGoogleFonts } from '@/hooks/useGoogleFonts';
import { createTemplate } from '@/services/api';
import { CreateTemplateFormData, TemplateVisibility } from '@d-id/types';
import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const GOOGLE_FONTS_ERROR_MESSAGE = 'Failed to load fonts. Please try again.';

const StyledTextField = styled(TextField)`
    & .MuiFormHelperText-root {
        font-size: 1.2rem; /* Adjust the font size as needed */
    }
`;

const ColorContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const hexColorPattern = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/;

const validationSchema = yup.object({
    srcProjectId: yup.string().required('Project ID is required'),
    templateName: yup.string().required('Template name is required'),
    tags: yup.array().of(yup.string().required('Each tag must be a string')).optional(),
    fonts: yup
        .object({
            primary: yup.string().required('Primary font is required'),
            secondary: yup.string().optional(),
            tertiary: yup.string().optional(),
        })
        .required(),
    colors: yup
        .object({
            primary: yup.string().required('Primary color is required').matches(hexColorPattern, 'Primary color must be a valid hex color'),
            secondary: yup.string().optional().matches(hexColorPattern, 'Secondary color must be a valid hex color'),
            tertiary: yup.string().optional().matches(hexColorPattern, 'Tertiary color must be a valid hex color'),
        })
        .required(),
    visibility: yup
        .mixed<TemplateVisibility>()
        .oneOf(Object.values(TemplateVisibility), 'Visibility must be SHOW or HIDE')
        .required('Visibility is required'),
    rank: yup.number().typeError('Rank must be a number').required('Rank is required'),
});

interface Props {
    projectId: string;
    handleClose: () => void;
}

export const SaveAsTemplateForm = ({ projectId, handleClose }: Props) => {
    const containerRef = useRef<HTMLFormElement | null>(null);
    const googleFontsQuery = useGoogleFonts();

    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<CreateTemplateFormData>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            srcProjectId: projectId,
            fonts: {
                primary: '',
            },
            colors: {
                primary: '',
            },
            visibility: TemplateVisibility.HIDE,
            rank: 0,
        },
    });

    const onSubmit = async (data: CreateTemplateFormData) => {
        await createTemplate(data);
        handleClose();
    };

    const handleTabKey = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            return;
        }

        if (e.key === 'Tab') {
            e.preventDefault();
        }

        e.stopPropagation();
    };

    // prevent closing modal
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('keydown', handleTabKey);
        }

        return () => {
            if (container) {
                container.removeEventListener('keydown', handleTabKey);
            }
        };
    }, []);

    return (
        <StyledForm ref={containerRef} onSubmit={handleSubmit(onSubmit)}>
            <StyledTextField
                fullWidth
                label="Template Name"
                variant="outlined"
                inputProps={{ ...register('templateName') }}
                error={!!errors.templateName}
                helperText={errors.templateName ? errors.templateName.message : ''}
            />

            <Controller
                name="tags"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        onChange={(_, newValue) => {
                            onChange(newValue);
                        }}
                        onBlur={onBlur}
                        value={value}
                        renderTags={(value, getTagProps) => {
                            return value.map((option, index) => (
                                <Chip label={option} {...getTagProps({ index })} sx={{ fontSize: '1.5rem', margin: '4px' }} />
                            ));
                        }}
                        renderInput={params => <StyledTextField {...params} label="Tags" variant="outlined" />}
                    />
                )}
            />

            <Typography variant="h6">Fonts</Typography>
            <Controller
                name="fonts.primary"
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        options={googleFontsQuery.data ?? []}
                        loading={googleFontsQuery.isLoading}
                        value={field.value}
                        onChange={(_, newValue) => field.onChange(newValue)}
                        disabled={googleFontsQuery.isError}
                        renderInput={params => (
                            <StyledTextField
                                {...params}
                                label="Primary Font"
                                variant={googleFontsQuery.isError ? 'standard' : 'outlined'}
                                error={!!errors.fonts?.primary || googleFontsQuery.isError}
                                helperText={
                                    errors.fonts?.primary?.message ?? (googleFontsQuery.isError ? GOOGLE_FONTS_ERROR_MESSAGE : undefined)
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {googleFontsQuery.isLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />
            <Controller
                name="fonts.secondary"
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        options={googleFontsQuery.data ?? []}
                        loading={googleFontsQuery.isLoading}
                        value={field.value}
                        onChange={(_, newValue) => field.onChange(newValue)}
                        disabled={googleFontsQuery.isError}
                        renderInput={params => (
                            <StyledTextField
                                {...params}
                                label="Secondary Font"
                                variant={googleFontsQuery.isError ? 'standard' : 'outlined'}
                                error={!!errors.fonts?.secondary || googleFontsQuery.isError}
                                helperText={
                                    errors.fonts?.secondary?.message ?? (googleFontsQuery.isError ? GOOGLE_FONTS_ERROR_MESSAGE : undefined)
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {googleFontsQuery.isLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />
            <Controller
                name="fonts.tertiary"
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        options={googleFontsQuery.data ?? []}
                        loading={googleFontsQuery.isLoading}
                        value={field.value}
                        onChange={(_, newValue) => field.onChange(newValue)}
                        disabled={googleFontsQuery.isError}
                        renderInput={params => (
                            <StyledTextField
                                {...params}
                                label="Tertiary Font"
                                variant={googleFontsQuery.isError ? 'standard' : 'outlined'}
                                error={!!errors.fonts?.tertiary || googleFontsQuery.isError}
                                helperText={
                                    errors.fonts?.tertiary?.message ?? (googleFontsQuery.isError ? GOOGLE_FONTS_ERROR_MESSAGE : undefined)
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {googleFontsQuery.isLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />

            <Typography variant="h6">Colors</Typography>
            <Controller
                name="colors.primary"
                control={control}
                render={({ field }) => (
                    <ColorContainer>
                        <ColorPicker size="48px" onChange={v => field.onChange(removeHexColorAlphaSuffix(v))} value={field.value || ''} />
                        <StyledTextField
                            {...field}
                            fullWidth
                            label="Primary Color"
                            variant="outlined"
                            placeholder="#FFFFFF"
                            error={!!errors.colors?.primary}
                            helperText={errors.colors?.primary ? errors.colors.primary.message : ''}
                        />
                    </ColorContainer>
                )}
            />

            <Controller
                name="colors.secondary"
                control={control}
                render={({ field }) => (
                    <ColorContainer>
                        <ColorPicker size="48px" onChange={v => field.onChange(removeHexColorAlphaSuffix(v))} value={field.value || ''} />
                        <StyledTextField {...field} fullWidth label="Secondary Color" variant="outlined" placeholder="#FFFFFF" />
                    </ColorContainer>
                )}
            />

            <Controller
                name="colors.tertiary"
                control={control}
                render={({ field }) => (
                    <ColorContainer>
                        <ColorPicker size="48px" onChange={v => field.onChange(removeHexColorAlphaSuffix(v))} value={field.value || ''} />
                        <StyledTextField {...field} fullWidth label="Tertiary Color" variant="outlined" placeholder="#FFFFFF" />
                    </ColorContainer>
                )}
            />

            <StyledTextField
                fullWidth
                label="Rank"
                variant="outlined"
                type="number"
                inputProps={{ ...register('rank') }}
                error={!!errors.rank}
                helperText={errors.rank ? errors.rank.message : ''}
            />

            <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                Save as Template
            </Button>
        </StyledForm>
    );
};
